import type { Process } from "../../api/types";
import { graphWithInstances, stepTimeStats } from "../processes/processGraphModel";
import { type Noticed, rankedNoticedFindings } from "../processes/processNoticed";
import {
  EMPTY_INSTANCE_SCOPE,
  buildInstanceScopeIndex,
  matchInstanceScope,
} from "../processes/processScopeModel";
import type { ProcessMetric } from "./processOverview";

export type OverviewInsight = {
  process: ProcessMetric;
  finding: Noticed | null;
  nameById: Map<string, string>;
};

/** Keep relevance anchored to the top three; use the same evidence as process detail. */
export const selectOverviewInsight = (
  metrics: ProcessMetric[],
  details: Process[],
  userIds: string[],
  from: string,
  to: string,
): OverviewInsight | null => {
  const top = metrics.filter(process => process.seconds > 0).slice(0, 3);
  if (!top.length) return null;
  const candidates = top.flatMap(process => {
    const detail = details.find(detail => detail.slug === process.slug);
    if (!detail) return [];
    const ids = new Set(
      matchInstanceScope(buildInstanceScopeIndex(detail.instances, detail.nodes), {
        ...EMPTY_INSTANCE_SCOPE,
        commonPaths: false,
        userIds,
        noUsers: userIds.length === 0,
        from: from || null,
        to: to || null,
      }).matchedIds,
    );
    const instances = detail.instances.filter(instance => ids.has(instance.id));
    // Prominence on Overview needs more support than a local step reading.
    if (instances.length < 5) return [];
    const graph = graphWithInstances(detail.nodes, instances);
    const nameById = new Map(detail.nodes.map(node => [node.id, node.name]));
    return rankedNoticedFindings({
      instances,
      stepStats: stepTimeStats(graph, graph.nodes),
      edges: graph.edges,
    })
      .filter(
        ({ finding }) =>
          finding.kind !== "dominant-path" &&
          finding.kind !== "no-dominant-path" &&
          finding.kind !== "branching",
      )
      .map(({ finding, score }) => ({ process, finding, nameById, score }));
  });
  // Equal scores retain total-work ordering, so selection stays deterministic.
  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0];
  return best
    ? { process: best.process, finding: best.finding, nameById: best.nameById }
    : { process: top[0], finding: null, nameById: new Map() };
};
